import { useContext, useMemo, useReducer, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSelectionComplete, wildCardsInSelection } from './cardSelection';
import { GameContext, type GameContextValue } from './gameContext';
import { INITIAL_MOCK_GAME_STATE, type MockGameAction, type MockGameState } from './mockGameTypes';
import { getCurrentPlayer, mockGameReducer } from './mockGameReducer';

export function MockGameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(mockGameReducer, INITIAL_MOCK_GAME_STATE);
  const navigate = useNavigate();
  const currentPlayer = getCurrentPlayer(state);

  const value = useMemo<GameContextValue>(
    () => ({
      runtimeMode: 'mock',
      connectionStatus: 'connected',
      state,
      currentPlayer,
      lastError: null,
      clearJoinError: () => dispatch({ type: 'CLEAR_JOIN_ERROR' }),
      clearError: () => {},

      createRoom(roomName, nickname, settings) {
        dispatch({ type: 'CREATE_ROOM', payload: { roomName, nickname, settings } });
        navigate('/lobby');
      },

      joinRoom(roomCode, nickname) {
        const next = mockGameReducer(state, { type: 'JOIN_ROOM', payload: { roomCode, nickname } });
        dispatch({ type: 'JOIN_ROOM', payload: { roomCode, nickname } });
        if (next.joinError) return false;
        navigate('/lobby');
        return true;
      },

      toggleReady() {
        dispatch({ type: 'TOGGLE_READY' });
      },

      startGame() {
        dispatch({ type: 'START_GAME' });
        navigate('/game');
      },

      toggleCard(id) {
        dispatch({ type: 'TOGGLE_CARD', payload: id });
      },

      clearSelection() {
        dispatch({ type: 'CLEAR_SELECTION' });
      },

      setWriteIn(text) {
        dispatch({ type: 'SET_WRITE_IN', payload: text });
      },

      submitAnswer() {
        if (state.selectedCardIds.length !== state.blackCardPick) return;
        const wilds = wildCardsInSelection(state.selectedCardIds, state.hand);
        if (wilds.length > 0 && !state.writeInText.trim()) {
          navigate('/write-in');
          return;
        }
        dispatch({ type: 'SUBMIT_ANSWER' });
        navigate('/judge');
      },

      confirmWriteIn() {
        const text = state.writeInText.trim();
        if (!text) return false;
        if (!isSelectionComplete(state.selectedCardIds, state.blackCardPick)) return false;
        dispatch({ type: 'CONFIRM_WRITE_IN' });
        dispatch({ type: 'SUBMIT_ANSWER' });
        navigate('/judge');
        return true;
      },

      pickWinner(playerId) {
        const winner = state.players.find((p) => p.id === playerId);
        if (!winner) return;
        const gameOver = winner.score + 1 >= state.settings.pointsToWin;
        dispatch({ type: 'PICK_WINNER', payload: playerId });
        navigate(gameOver ? '/game-over' : '/round-winner');
      },

      pickWinnerByIndex(revealIndex) {
        const sub = state.submissions[revealIndex];
        if (!sub) return;
        const winner = state.players.find((p) => p.id === sub.playerId);
        if (!winner) return;
        const gameOver = winner.score + 1 >= state.settings.pointsToWin;
        dispatch({ type: 'PICK_WINNER', payload: sub.playerId });
        navigate(gameOver ? '/game-over' : '/round-winner');
      },

      nextRound() {
        dispatch({ type: 'NEXT_ROUND' });
        navigate('/game');
      },

      playAgain() {
        dispatch({ type: 'PLAY_AGAIN' });
        navigate('/lobby');
      },

      newRoom() {
        dispatch({ type: 'NEW_ROOM' });
        navigate('/create');
      },

      reset() {
        dispatch({ type: 'RESET' });
        navigate('/');
      },
    }),
    [state, currentPlayer, navigate],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

/** @deprecated Import MockGameProvider from mockGameStore or use RuntimeGameProvider. */
export function useMockGameFromStore() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useMockGame must be used within MockGameProvider');
  return ctx;
}

export type { MockGameAction, MockGameState };
